/**
 * Define the different parts of your state here
 * using https://recoiljs.org/
 */
import { atom, selector, SetterOrUpdater } from 'recoil'

import db from './db/db'
import { AnalyticsService } from './services/AnalyticsService'
import { AnalyticsEvents } from './types/AnalyticsEvents'
import Database from './types/Database'
import { getSundayBeforeMonthStart } from './utils/calendarHelpers'
import {
  generateDateRange,
  getMinimumDate,
  calculateBookingDayRange
} from './utils/dateRangeHelpers'
import { formatDateToString } from './utils/FormatHelpers'
import { validateEmail } from './utils/validationHelpers'

export interface ISessionState {
  user: Database.User | null
  isAuthenticating: boolean
  isLoadingData: boolean
  desks: Database.Desk[] | undefined
  offices: Database.Office[] | undefined
  selectedOffice: Database.Office | undefined
  officeDesks: Database.Desk[] | undefined
  selectedFromDate: string
  selectedRecurAmount: number
  selectedRecurUnit: 'Once off' | 'Same days every week'
  selectedWeekDays: number[] // 0=Sunday, 1=Monday, etc. for weekly bookings
  bookings: Database.Booking[] | undefined
  userBookings: Database.Booking[] | undefined
  deskToEdit: Database.Desk | null
  highlightedDates: string[] // Array of date strings (YYYY-MM-DD) to highlight
  onboarding: {
    isComplete: boolean
    currentStep: Database.OnboardingStep
    startedAt: Date | null
    completedAt: Date | null
    skippedDeskCreation: boolean
    hasSeenPointers: {
      officeForm: boolean
      deskForm: boolean
    }
    hasVisitedPages: {
      calendar: boolean
      booking: boolean
    }
  }
}

export interface ITeamState {
  teamMembers: Database.TeamMember[]
  teamInvitations: Database.TeamInvitation[]
  sentInvitations: Database.TeamInvitation[]
  receivedInvitations: Database.TeamInvitation[]
  teamBookings: Database.EnrichedTeamBooking[]
  isLoadingTeam: boolean
}

export interface IAdminState {
  statistics: Database.SystemStatistics | null
  users: Database.User[]
  offices: Database.Office[]
  auditLogs: Database.AuditLogEntry[]
  isLoadingStatistics: boolean
  isLoadingUsers: boolean
  isLoadingOffices: boolean
  error: string | null
}

export const sessionState = atom<ISessionState>({
  key: 'session',
  default: {
    isAuthenticating: true,
    isLoadingData: false,
    user: null,
    desks: [],
    offices: [],
    selectedOffice: undefined,
    officeDesks: [],
    selectedFromDate: formatDateToString(getSundayBeforeMonthStart(new Date())),
    selectedRecurAmount: 35,
    selectedRecurUnit: 'Once off',
    selectedWeekDays: [],
    bookings: [],
    userBookings: [],
    deskToEdit: null,
    highlightedDates: [],
    onboarding: {
      isComplete: false,
      currentStep: 'not_started',
      startedAt: null,
      completedAt: null,
      skippedDeskCreation: false,
      hasSeenPointers: {
        officeForm: false,
        deskForm: false
      },
      hasVisitedPages: {
        calendar: false,
        booking: false
      }
    }
  }
})

export const teamState = atom<ITeamState>({
  key: 'team',
  default: {
    teamMembers: [],
    teamInvitations: [],
    sentInvitations: [],
    receivedInvitations: [],
    teamBookings: [],
    isLoadingTeam: false
  }
})

export const adminState = atom<IAdminState>({
  key: 'admin',
  default: {
    statistics: null,
    users: [],
    offices: [],
    auditLogs: [],
    isLoadingStatistics: false,
    isLoadingUsers: false,
    isLoadingOffices: false,
    error: null
  }
})

// Admin state selectors
export const filteredAndSortedUsersSelector = selector({
  key: 'filteredAndSortedUsers',
  get: ({ get }) => {
    const admin = get(adminState)
    return admin.users
  }
})

export const statisticsLoadingSelector = selector({
  key: 'statisticsLoading',
  get: ({ get }) => {
    const admin = get(adminState)
    return admin.isLoadingStatistics
  }
})

// Onboarding state selectors
export const onboardingStateSelector = selector({
  key: 'onboardingState',
  get: ({ get }) => {
    const session = get(sessionState)
    return session.onboarding
  }
})

export const isOnboardingCompleteSelector = selector({
  key: 'isOnboardingComplete',
  get: ({ get }) => {
    const session = get(sessionState)
    return session.onboarding.isComplete
  }
})

export const currentOnboardingStepSelector = selector({
  key: 'currentOnboardingStep',
  get: ({ get }) => {
    const session = get(sessionState)
    return session.onboarding.currentStep
  }
})

export const hasVisitedPageSelector = selector({
  key: 'hasVisitedPage',
  get: ({ get }) => {
    const session = get(sessionState)
    return session.onboarding.hasVisitedPages
  }
})

// This function is now available in dateRangeHelpers.ts as generateDateRange

const refreshBookingsForDate = async (
  setState: SetterOrUpdater<ISessionState>,
  currentState: ISessionState,
  newDate: Date
) => {
  var dateRangeBookins = await findExistingBookings(
    newDate,
    currentState.selectedRecurAmount,
    currentState.selectedRecurUnit,
    currentState.offices ?? []
  )

  setState(old => ({
    ...old,
    selectedFromDate: formatDateToString(newDate),
    bookings: dateRangeBookins
  }))
}

const findExistingBookings = async (
  selectedFromDate: Date,
  number: number,
  unit: 'Once off' | 'Same days every week',
  myOffices: Database.Office[],
  selectedWeekDays: number[] = []
) => {
  var Difference_In_Days = calculateBookingDayRange(unit, number)

  // To calculate the no. of days between two dates
  if (Difference_In_Days < 0) return []

  const dateRange: Date[] = generateDateRange(
    selectedFromDate,
    unit,
    number,
    selectedWeekDays
  )
  const minDate = getMinimumDate(dateRange)

  if (myOffices.length <= 0) {
    return []
  }
  console.log('Offices:', myOffices)
  var bookings = await db.query({
    collection: 'booking',
    where: [
      ['forDate', '>=', minDate],
      ['oid', 'in', myOffices.map(x => x.oid)]
    ]
  })
  console.log('Bookings:', bookings)
  return bookings
}

const myDesks = async (uid: string | undefined) => {
  if (!uid) return undefined

  return await db.query({
    collection: 'desks',
    where: ['uid', '==', uid]
  })
}

const getDesksForManager = async (user: Database.User | undefined) => {
  if (!user || !user.uid) return []

  // Get desks created by the user
  const userCreatedDesks = (await myDesks(user.uid)) || []

  // Get offices the user manages
  let managedOffices: Database.OfficeManager[] = []
  try {
    if (
      user.email &&
      typeof user.email === 'string' &&
      user.email.trim() !== ''
    ) {
      managedOffices = await db.query({
        collection: 'officeManagers',
        where: ['email', '==', user.email]
      })
    }
  } catch (error) {
    console.warn('Error fetching managed offices:', error)
  }

  // Get all desks from managed offices
  let managedOfficeDesks: Database.Desk[] = []
  if (managedOffices.length > 0) {
    const managedOfficeIds = managedOffices.map(x => x.officeId).filter(Boolean)
    if (managedOfficeIds.length > 0) {
      try {
        managedOfficeDesks = await db.query({
          collection: 'desks',
          where: ['office', 'in', managedOfficeIds]
        })
      } catch (error) {
        console.warn('Error fetching managed office desks:', error)
      }
    }
  }

  // Combine and deduplicate desks (in case user created desks in offices they manage)
  const allDesks = [...userCreatedDesks, ...managedOfficeDesks]
  const uniqueDesks = allDesks.filter(
    (desk, index, self) => index === self.findIndex(d => d.id === desk.id)
  )

  return uniqueDesks
}

const meUser = async (uid: string | undefined) => {
  if (!uid) return undefined

  return await db.query({
    collection: 'users',
    where: ['uid', '==', uid]
  })
}

const myOffices = async (user: Database.User | undefined) => {
  if (!user) return undefined

  var invites =
    user.email && typeof user.email === 'string' && user.email.trim() !== ''
      ? await db.query({
          collection: 'invite',
          where: ['email', '==', user.email]
        })
      : []

  // Get offices the user can manage
  var managedOffices: Database.OfficeManager[] = []
  try {
    if (
      user.email &&
      typeof user.email === 'string' &&
      user.email.trim() !== ''
    ) {
      managedOffices = await db.query({
        collection: 'officeManagers',
        where: ['email', '==', user.email]
      })
    }
  } catch (error) {
    console.warn('Error fetching managed offices:', error)
  }

  // Update any pending office manager records for this user's email
  if (
    user.email &&
    typeof user.email === 'string' &&
    user.email.trim() !== ''
  ) {
    try {
      var pendingManagerRecords = await db.query({
        collection: 'officeManagers',
        where: ['email', '==', user.email]
      })

      // Filter for records with empty or undefined userId and update them
      var recordsToUpdate = pendingManagerRecords.filter(
        record =>
          !record.userId ||
          record.userId === '' ||
          record.userId === undefined ||
          record.userId === null
      )

      // Update pending records with the user's UID and migrate ID to deterministic format
      for (const record of recordsToUpdate) {
        if (record.id && user.email) {
          const newId = `${record.officeId}_${user.email}`
          if (newId !== record.id) {
            // Create new doc with deterministic ID, then delete old
            const newManagerData = {
              officeId: record.officeId,
              userId: user.uid,
              email: record.email || user.email,
              addedBy: record.addedBy || user.uid,
              addedOnDate: record.addedOnDate || new Date()
            }
            // Use setDoc directly for custom ID
            const { getFirestore, doc, setDoc } = require('firebase/firestore')
            const firestore = getFirestore()
            await setDoc(
              doc(firestore, 'officeManagers', newId),
              newManagerData
            )
            await db.deleteItem('officeManagers', record.id)
          } else {
            await db.updateItem('officeManagers', record.id, {
              userId: user.uid
            })
          }
        }
      }
    } catch (error) {
      console.warn('Error updating pending office manager records:', error)
    }
  }

  if (
    !user.officeids ||
    (user.officeids.length === 0 &&
      invites.length === 0 &&
      managedOffices.length === 0)
  )
    return []

  var myCreatedOffices =
    user.officeids.length > 0
      ? await db.query({
          collection: 'offices',
          where: ['oid', 'in', user.officeids]
        })
      : []

  var invitedToOffices =
    invites.length > 0
      ? await db.query({
          collection: 'offices',
          where: ['oid', 'in', invites.map(x => x.oid)]
        })
      : []

  var managedOfficeIds = managedOffices.map(x => x.officeId).filter(Boolean)
  var managedOfficeDetails =
    managedOfficeIds.length > 0
      ? await db.query({
          collection: 'offices',
          where: ['oid', 'in', managedOfficeIds]
        })
      : []

  // Combine all offices and deduplicate by oid
  const allOffices = myCreatedOffices
    .concat(invitedToOffices)
    .concat(managedOfficeDetails)
  const uniqueOffices = allOffices.filter(
    (office, index, self) => index === self.findIndex(o => o.oid === office.oid)
  )

  // Filter out soft-deleted offices for regular users
  const activeOffices = uniqueOffices.filter(office => !office.isDeleted)

  return activeOffices
}

const myBookings = async (uid: string): Promise<Database.Booking[]> => {
  // Query by bookingOwnerId to include proxy bookings
  // For bookings created before proxy booking feature, bookingOwnerId may not exist,
  // so we also query by uid as a fallback
  const ownerBookings = await db.query({
    collection: 'booking',
    where: ['bookingOwnerId', '==', uid]
  })

  // Also get bookings where uid matches (for backward compatibility with old bookings)
  const legacyBookings = await db.query({
    collection: 'booking',
    where: ['uid', '==', uid]
  })

  // Merge and deduplicate bookings by id
  const allBookings = [...ownerBookings, ...legacyBookings]
  const uniqueBookings = Array.from(
    new Map(allBookings.map(booking => [booking.id, booking])).values()
  )

  return uniqueBookings
}

const officeDesks = async (office: Database.Office) => {
  return await db.query({
    collection: 'desks',
    where: ['office', '==', office.oid]
  })
}

const getNewSession = async (
  setSession: SetterOrUpdater<ISessionState>,
  uid: string,
  from: Date,
  amount: number,
  unit: 'Once off' | 'Same days every week'
) => {
  // Set loading flag immediately
  setSession(old => ({
    ...old,
    isLoadingData: true
  }))

  // Retry logic for new user signup - wait for user record to be created
  let user = await meUser(uid)
  let retries = 0
  const maxRetries = 50 // 5 seconds total (50 * 100ms)

  while (!user || user.length === 0) {
    if (retries >= maxRetries) {
      console.warn(
        `User record not found after ${maxRetries} retries for uid: ${uid}`
      )
      break
    }

    console.log(
      `Waiting for user record to be created (attempt ${
        retries + 1
      }/${maxRetries})...`
    )
    await new Promise(resolve => setTimeout(resolve, 100)) // Wait 100ms
    user = await meUser(uid)
    retries++
  }

  if (user && user.length > 0) {
    console.log(`✅ User record found after ${retries} retries`)
  } else {
    console.error(
      `❌ User record NOT found after ${maxRetries} retries for uid: ${uid}`
    )
  }

  var foundUser = user ? user[0] : undefined
  console.log(
    'getNewSession - foundUser:',
    foundUser
      ? `uid: ${foundUser.uid}, offices: ${foundUser.officeids?.length || 0}`
      : 'undefined'
  )

  var desks = await getDesksForManager(foundUser)
  var offices = await myOffices(foundUser)
  console.log('getNewSession - offices loaded:', offices?.length || 0)

  var selectedOfficeDesks: Database.Desk[] =
    offices && !!offices.length ? await officeDesks(offices[0]) : []
  var bookings = await findExistingBookings(from, amount, unit, offices ?? [])
  var userBookings: Database.Booking[] = await myBookings(uid)

  const newSession = {
    isAuthenticating: false,
    isLoadingData: false,
    user: user ? user[0] : null,
    desks: desks,
    offices: offices,
    selectedOffice: offices?.length ? offices[0] : undefined,
    officeDesks: selectedOfficeDesks,
    bookings: bookings,
    selectedFromDate: formatDateToString(from),
    selectedRecurAmount: amount,
    selectedRecurUnit: unit,
    selectedWeekDays: [],
    userBookings: userBookings,
    deskToEdit: null,
    highlightedDates: [],
    onboarding: {
      isComplete: false,
      currentStep: 'not_started' as Database.OnboardingStep,
      startedAt: null,
      completedAt: null,
      skippedDeskCreation: false,
      hasSeenPointers: {
        officeForm: false,
        deskForm: false
      },
      hasVisitedPages: {
        calendar: false,
        booking: false
      }
    }
  }
  console.log(
    'getNewSession - session created with user:',
    newSession.user ? `uid: ${newSession.user.uid}` : 'null'
  )
  setSession(newSession)
  return newSession
}

const loadTeamMembers = async (
  userId: string
): Promise<Database.TeamMember[]> => {
  if (!userId) return []

  try {
    // Query for team members where current user is userId1
    const asUserId1 = await db.query({
      collection: 'teamMembers',
      where: ['userId1', '==', userId]
    })

    // Query for team members where current user is userId2
    const asUserId2 = await db.query({
      collection: 'teamMembers',
      where: ['userId2', '==', userId]
    })

    // Combine both results
    return [...asUserId1, ...asUserId2]
  } catch (error) {
    console.error('Error loading team members:', error)
    throw new Error(
      'Unable to load team members. Please check your connection and try again.'
    )
  }
}

const loadTeamInvitations = async (
  userId: string
): Promise<{
  sent: Database.TeamInvitation[]
  received: Database.TeamInvitation[]
}> => {
  if (!userId) return { sent: [], received: [] }

  try {
    // Query for invitations sent by current user
    const sent = await db.query({
      collection: 'teamInvitations',
      where: ['inviterId', '==', userId]
    })

    // Query for invitations received by current user
    const received = await db.query({
      collection: 'teamInvitations',
      where: ['inviteeId', '==', userId]
    })

    return { sent, received }
  } catch (error) {
    console.error('Error loading team invitations:', error)
    throw new Error(
      'Unable to load team invitations. Please check your connection and try again.'
    )
  }
}

const sendTeamInvitation = async (
  inviterId: string,
  inviterEmail: string,
  inviterName: string,
  inviteeEmail: string
): Promise<void> => {
  // Validate email format
  if (!validateEmail(inviteeEmail)) {
    throw new Error('Please enter a valid email address')
  }

  // Check if invitee user exists by email
  const inviteeUsers = await db.query({
    collection: 'users',
    where: ['email', '==', inviteeEmail]
  })

  if (inviteeUsers.length === 0) {
    throw new Error(
      `No user found with email ${inviteeEmail}. They must create an account first.`
    )
  }

  const inviteeUser = inviteeUsers[0]
  const inviteeId = inviteeUser.uid

  if (!inviteeId) {
    throw new Error('Invitee user ID not found')
  }

  // Check for existing team relationship
  // Generate deterministic ID for team member record
  const [sortedUserId1, sortedUserId2] = [inviterId, inviteeId].sort()
  const teamMemberId = `${sortedUserId1}_${sortedUserId2}`

  const existingTeamMember = await db.getById('teamMembers', teamMemberId)

  if (existingTeamMember) {
    throw new Error('You are already team members with this user')
  }

  // Check for pending invitation
  const existingInvitations = await db.query({
    collection: 'teamInvitations',
    where: [
      ['inviterId', '==', inviterId],
      ['inviteeId', '==', inviteeId]
    ]
  })

  if (existingInvitations.length > 0) {
    throw new Error('You have already sent an invitation to this user')
  }

  // Create invitation record with all required fields
  await db.addItem('teamInvitations', {
    inviterId,
    inviterEmail,
    inviterName,
    inviteeId,
    inviteeEmail,
    createdAt: new Date(),
    status: 'pending' as const
  })

  // Track team invitation sent event
  AnalyticsService.trackEvent(AnalyticsEvents.TEAM_INVITE_SENT, {
    invitation_type: 'team'
  })
}

const acceptTeamInvitation = async (
  invitationId: string,
  invitation: Database.TeamInvitation
): Promise<void> => {
  // Create bidirectional team member record with deterministic ID
  const [sortedUserId1, sortedUserId2] = [
    invitation.inviterId,
    invitation.inviteeId
  ].sort()
  const teamMemberId = `${sortedUserId1}_${sortedUserId2}`

  // Create team member relationship
  await db.addItem('teamMembers', {
    id: teamMemberId,
    userId1: sortedUserId1,
    userId2: sortedUserId2,
    createdAt: new Date(),
    createdBy: invitation.inviteeId // The person accepting the invitation
  } as any)

  // Delete the invitation record
  await db.deleteItem('teamInvitations', invitationId)

  // Track team invitation accepted event
  AnalyticsService.trackEvent(AnalyticsEvents.TEAM_INVITE_ACCEPTED, {
    invitation_type: 'team'
  })
}

const removeTeamMember = async (
  currentUserId: string,
  teamMemberId: string
): Promise<void> => {
  // Delete the team member record
  // The bidirectional relationship is represented by a single record,
  // so deleting it removes the relationship for both users
  await db.deleteItem('teamMembers', teamMemberId)

  // Track team member removed event
  AnalyticsService.trackEvent(AnalyticsEvents.TEAM_MEMBER_REMOVED, {})
}

const rejectTeamInvitation = async (invitationId: string): Promise<void> => {
  // Delete the invitation record without creating a team relationship
  await db.deleteItem('teamInvitations', invitationId)

  // Track team invitation rejected event
  AnalyticsService.trackEvent(AnalyticsEvents.INVITE_REJECTED, {
    invitation_type: 'team'
  })
}

const loadTeamBookings = async (
  userId: string
): Promise<Database.EnrichedTeamBooking[]> => {
  if (!userId) return []

  try {
    // Get team members for current user
    const teamMembers = await loadTeamMembers(userId)

    // Extract all team member user IDs (including current user)
    const teamMemberIds = new Set<string>([userId])
    teamMembers.forEach(member => {
      teamMemberIds.add(member.userId1)
      teamMemberIds.add(member.userId2)
    })

    // Convert to array for querying
    const memberIdsArray = Array.from(teamMemberIds)

    // Query bookings for all team members
    // Note: Firestore 'in' operator has a limit of 10 values
    // For teams > 10 members, we need to batch the queries
    const allBookings: Database.Booking[] = []

    for (let i = 0; i < memberIdsArray.length; i += 10) {
      const batch = memberIdsArray.slice(i, i + 10)
      const batchBookings = await db.query({
        collection: 'booking',
        where: ['bookingOwnerId', 'in', batch]
      })
      allBookings.push(...batchBookings)
    }

    // Also query for bookings without bookingOwnerId (legacy bookings)
    // where uid matches team member IDs
    for (let i = 0; i < memberIdsArray.length; i += 10) {
      const batch = memberIdsArray.slice(i, i + 10)
      const legacyBookings = await db.query({
        collection: 'booking',
        where: ['uid', 'in', batch]
      })
      // Only include legacy bookings that don't have bookingOwnerId set
      const filteredLegacy = legacyBookings.filter(b => !b.bookingOwnerId)
      allBookings.push(...filteredLegacy)
    }

    // Filter for upcoming bookings (forDate >= today)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const upcomingBookings = allBookings.filter(booking => {
      if (!booking.forDate) return false
      const bookingDate =
        booking.forDate instanceof Date
          ? booking.forDate
          : new Date(booking.forDate)
      return bookingDate >= today
    })

    // Sort by forDate ascending
    upcomingBookings.sort((a, b) => {
      const dateA = a.forDate instanceof Date ? a.forDate : new Date(a.forDate!)
      const dateB = b.forDate instanceof Date ? b.forDate : new Date(b.forDate!)
      return dateA.getTime() - dateB.getTime()
    })

    // Get all unique user IDs we need to fetch (owners and creators)
    const userIdsToFetch = new Set<string>()
    upcomingBookings.forEach(booking => {
      const ownerId = booking.bookingOwnerId || booking.uid
      if (ownerId) userIdsToFetch.add(ownerId)
      if (booking.bookingCreatorId && booking.isProxyBooking) {
        userIdsToFetch.add(booking.bookingCreatorId)
      }
    })

    // Fetch all users in batches
    const userMap = new Map<string, Database.User>()
    const userIdsArray = Array.from(userIdsToFetch)

    for (let i = 0; i < userIdsArray.length; i += 10) {
      const batch = userIdsArray.slice(i, i + 10)
      const users = await db.query({
        collection: 'users',
        where: ['uid', 'in', batch]
      })
      users.forEach(user => {
        if (user.uid) userMap.set(user.uid, user)
      })
    }

    // Enrich bookings with team member names and emails
    const enrichedBookings: Database.EnrichedTeamBooking[] =
      upcomingBookings.map(booking => {
        const ownerId = booking.bookingOwnerId || booking.uid
        const owner = ownerId ? userMap.get(ownerId) : undefined

        const teamMemberName = owner
          ? `${owner.firstname || ''} ${owner.lastname || ''}`.trim() ||
            owner.email ||
            'Unknown'
          : 'Unknown'

        const teamMemberEmail = owner?.email || ''

        let creatorName: string | undefined
        if (booking.isProxyBooking && booking.bookingCreatorId) {
          const creator = userMap.get(booking.bookingCreatorId)
          if (creator) {
            creatorName =
              `${creator.firstname || ''} ${creator.lastname || ''}`.trim() ||
              creator.email ||
              'Unknown'
          }
        }

        return {
          ...booking,
          teamMemberName,
          teamMemberEmail,
          creatorName
        }
      })

    return enrichedBookings
  } catch (error) {
    console.error('Error loading team bookings:', error)
    throw new Error(
      'Unable to load team bookings. Please check your connection and try again.'
    )
  }
}

export { getNewSession }
export { officeDesks }
export { findExistingBookings }
export { refreshBookingsForDate }
export { getDesksForManager }
export { myBookings }
export { loadTeamMembers }
export { loadTeamInvitations }
export { loadTeamBookings }
export { sendTeamInvitation }
export { acceptTeamInvitation }
export { rejectTeamInvitation }
export { removeTeamMember }
// dateRangeDates is now available as generateDateRange in dateRangeHelpers.ts
